import { QuestionInterface } from '@api/assessmentQuestionnaireApiHandler';
import { useAppDispatch } from '@hooks/useAppDispatch';
import { useAppSelector } from '@hooks/useAppSelector';
import { MotionText } from '@shared/atoms';
import { ShareThoughtDrawer, SystemButton } from '@shared/molecules';
import React, { useState } from 'react';
import { answerLikertMultipleQuestion, answerQuestion } from 'store/Slices/QuestionnaireSliceV2';
import styles from './index.module.scss';

interface LikertMultipleQuestionsProps {
  questions: QuestionInterface[];
  questionsCount?: number;
  answerDisplayingType?: 'row' | 'column';
  shareThoughtEnabled?: boolean;
  onComplete?: () => void;
}

export const LikertMultipleQuestions: React.FC<LikertMultipleQuestionsProps> = ({
  questions,
  questionsCount = 4,
  answerDisplayingType = 'row',
  shareThoughtEnabled,
  onComplete,
}) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [thoughtOpen, setThoughtOpen] = useState(false);

  const dispatch = useAppDispatch();
  const { answers } = useAppSelector((state) => state.questionnaireV2 || {});
  const totalPages = Math.ceil(questions.length / questionsCount);

  const currentQuestions = questions.slice(
    currentPage * questionsCount,
    (currentPage + 1) * questionsCount,
  );

  const handleOptionSelect = (questionId: number, optionId: number, answerScore: number) => {
    dispatch(
      answerLikertMultipleQuestion({
        questionId,
        answerId: Number(optionId),
        answerScore: Number(answerScore),
      }),
    );
  };

  // TODO: Handle submitting answers
  const goToNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    } else if (onComplete) {
      onComplete();
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const isCurrentPageCompleted = () => currentQuestions.every((question) => answers[question.id]);

  return (
    <div className={styles.container}>
      <div className={styles.questionsContainer}>
        {currentQuestions.map((question) => (
          <div key={question.id} className={styles.questionCard}>
            <div className={styles.questionContainer}>
              <h3 className={styles.questionText}>{question?.questionText || ''}</h3>
              {shareThoughtEnabled && (
                <MotionText
                  as="p"
                  text="Share thought"
                  className={styles.shareThoughtText}
                  onClick={() => setThoughtOpen(true)}
                />
              )}
            </div>

            {shareThoughtEnabled && (
              <ShareThoughtDrawer
                open={thoughtOpen}
                handleClose={() => setThoughtOpen(false)}
                handleOpen={() => setThoughtOpen(true)}
                questionId={question.id}
                questionLabel={question?.questionText || ''}
              />
            )}

            <div
              className={`${styles.optionsContainer} ${
                answerDisplayingType === 'row' ? styles.rowDisplay : styles.columnDisplay
              }`}
            >
              {question &&
                question?.questionnairesV2QuestionResponseScales &&
                question.questionnairesV2QuestionResponseScales?.questionnairesV2QuestionResponseScaleOptions?.map(
                  (option) => (
                    <button
                      key={option.id}
                      className={`${styles.optionButton} ${
                        answers[question.id]?.answerId === option.id ? styles.selected : ''
                      }`}
                      onClick={() => handleOptionSelect(question.id, option.id, option.score)}
                    >
                      {option.optionText}
                    </button>
                  ),
                )}
            </div>
          </div>
        ))}
      </div>

      <div className={styles.navigationContainer}>
        <SystemButton
          onClick={goToPrevPage}
          disabled={currentPage === 0}
          variant="outlinedPrimarySkyBlue"
        >
          Previous
        </SystemButton>

        <div className={styles.pageIndicator}>
          {Array.from({ length: totalPages }).map((_, index) => (
            <span
              key={index}
              className={`${styles.pageIndicatorDot} ${index === currentPage ? styles.active : ''}`}
            />
          ))}
        </div>

        <SystemButton onClick={goToNextPage} disabled={!isCurrentPageCompleted()}>
          {currentPage === totalPages - 1 ? 'Finish' : 'Next'}
        </SystemButton>
      </div>
    </div>
  );
};

LikertMultipleQuestions.defaultProps = {
  questionsCount: 4,
  answerDisplayingType: 'row',
  shareThoughtEnabled: false,
  onComplete: () => null,
};
